import { apiFetch } from './api';
import type { PrazoLembrete, PrazoSilencio } from '../lib/prazosChat';

const apiUrl = import.meta.env.VITE_API_URL;

// ... (interfaces)

/* As três marcas da caixa de entrada (arquivar, não lida, fixar) são o mesmo PATCH com um booleano.
   A API recusa com a frase pronta em `mensagem` ("Você já tem 3 conversas fixadas…"), e é ela que sobe. */
const marcarConversa = async (
    idConversa: number,
    rota: 'Arquivar' | 'NaoLida' | 'Fixar',
    corpo: Record<string, boolean>,
    padrao: string,
): Promise<void> => {
    const response = await apiFetch(`${apiUrl}/api/Chat/Conversa/${idConversa}/${rota}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(corpo),
    });

    if (!response.ok) {
        const erro = await response.json().catch(() => null);
        throw new Error(erro?.mensagem || padrao);
    }
};

/* Silenciar e lembrar mandam o NOME do prazo ("8h", "amanha"…), não a data: quem calcula é a API, no
   relógio de Brasília. Prazo nulo desfaz. Devolve o instante que a API calculou (ms), para a tela
   trocar o palpite local pelo valor dela. */
const agendarConversa = async (
    idConversa: number,
    rota: 'Silenciar' | 'Lembrar',
    prazo: string | null,
    padrao: string,
): Promise<number | null> => {
    const response = await apiFetch(`${apiUrl}/api/Chat/Conversa/${idConversa}/${rota}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prazo }),
    });

    if (!response.ok) {
        const erro = await response.json().catch(() => null);
        throw new Error(erro?.mensagem || padrao);
    }

    const resultado = await response.json().catch(() => null);
    const ate = resultado?.dados?.ate;
    return ate ? new Date(ate).getTime() : null;
};

export const ChatService = {
    getConversationSummary: async (conversationId: number): Promise<ConversationSummaryResponse> => {
        try {
            const response = await apiFetch(`${apiUrl}/api/Conversa/Resumo/${conversationId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            // A recusa da API (400) traz o motivo em `mensagem` — ex.: "Esta conversa
            // não possui mensagens para resumir." Devolver em vez de lançar, senão
            // a tela só consegue mostrar um erro genérico.
            const data = await response.json().catch(() => null);
            if (!response.ok && !data?.mensagem) {
                throw new Error(`Erro ao buscar resumo: ${response.statusText}`);
            }
            return data;
        } catch (error) {
            console.error('Erro no ChatService.getConversationSummary:', error);
            throw error;
        }
    },

    enviarMidia: async (conversationId: number, file: File, tipoMidia: string): Promise<any> => {
        try {
            const formData = new FormData();
            formData.append('Arquivo', file);

            // Append query parameters to the URL
            const url = new URL(`${apiUrl}/api/Chat/Conversa/${conversationId}/EnviarMidia`);
            url.searchParams.append('TipoMidia', tipoMidia);

            const response = await apiFetch(url.toString(), {
                method: 'POST',
                // Content-Type is automatically set by browser for FormData with boundary
                body: formData,
            });

            if (!response.ok) {
                let errorMessage = `Erro ao enviar mídia: ${response.statusText}`;
                try {
                    const errorData = await response.json();
                    if (errorData?.mensagem) {
                        errorMessage = errorData.mensagem;
                    }
                } catch (e) {
                    // Ignore parsing error
                }
                throw new Error(errorMessage);
            }

            // Return response if needed, or just void
            return true;
        } catch (error) {
            console.error('Erro no ChatService.enviarMidia:', error);
            throw error;
        }
    },

    atualizarTipoLead: async (idConversa: number, tipoLeadId: number): Promise<any> => {
        try {
            const body = { tipoLeadId };
            const response = await apiFetch(`${apiUrl}/api/Chat/Conversa/${idConversa}/TipoLead`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                let errorMessage = `Erro ao atualizar tipo de lead: ${response.statusText}`;
                try {
                    const errorData = await response.json();
                    if (errorData?.mensagem) {
                        errorMessage = errorData.mensagem;
                    }
                } catch (e) {
                    // Ignore
                }
                throw new Error(errorMessage);
            }

            return true;
        } catch (error) {
            console.error('Erro no ChatService.atualizarTipoLead:', error);
            throw error;
        }
    },

    arquivarConversa: (idConversa: number, arquivada: boolean): Promise<void> =>
        marcarConversa(idConversa, 'Arquivar', { arquivada }, 'Não foi possível arquivar a conversa.'),

    marcarNaoLida: (idConversa: number, naoLida: boolean): Promise<void> =>
        marcarConversa(idConversa, 'NaoLida', { naoLida }, 'Não foi possível marcar a conversa.'),

    fixarConversa: (idConversa: number, fixada: boolean): Promise<void> =>
        marcarConversa(idConversa, 'Fixar', { fixada }, 'Não foi possível fixar a conversa.'),

    silenciarConversa: (idConversa: number, prazo: PrazoSilencio | null): Promise<number | null> =>
        agendarConversa(idConversa, 'Silenciar', prazo, 'Não foi possível silenciar a conversa.'),

    lembrarConversa: (idConversa: number, prazo: PrazoLembrete | null): Promise<number | null> =>
        agendarConversa(idConversa, 'Lembrar', prazo, 'Não foi possível guardar o lembrete.'),
};
